<template>
  <section id="compliance" class="compliance bg-decoration">
    <div class="container">
      <div class="compliance-content">
        <div class="compliance-text">
          <SectionHeader
            subtitle-key="compliance.subtitle"
            title-key="compliance.title"
          />
          <p class="compliance-description">
            {{ $t('compliance.description') }}
          </p>
          <p class="compliance-description">
            {{ $t('compliance.description2') }}
          </p>
          <div class="compliance-principles">
            <div class="principle-item glass glass-glow" v-for="(principle, index) in principles" :key="index">
              <div class="principle-icon glass-sm">
                <span v-html="icons[principle.key]"></span>
              </div>
              <div class="principle-content">
                <h4>{{ $t(`compliance.principles.${principle.key}.title`) }}</h4>
                <p>{{ $t(`compliance.principles.${principle.key}.description`) }}</p>
              </div>
            </div>
          </div>
          <div class="compliance-actions">
            <router-link to="/compliance-framework" class="btn btn-outline">{{ $t('compliance.learnMore') }}</router-link>
          </div>
        </div>
        <div class="compliance-image">
          <div class="image-card glass glass-glow">
            <img src="../../.image/compliance/compliance.png" alt="Compliance" class="compliance-img">
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref } from 'vue'
import SectionHeader from './common/SectionHeader.vue'
import { iconMap } from '@/assets/icons.js'

const icons = iconMap

const principles = ref([
  { key: 'integrity' },
  { key: 'confidentiality' },
  { key: 'independence' },
  { key: 'standards' }
])
</script>

<style scoped>
.compliance {
  padding: 120px 0;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e8f0f7 100%);
  position: relative;
}

.compliance::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background:
    radial-gradient(circle at 25% 25%, rgba(52, 152, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 75% 75%, rgba(15, 39, 64, 0.06) 0%, transparent 50%);
  pointer-events: none;
}

.compliance-content {
  display: grid;
  grid-template-columns: 1.3fr 0.7fr;
  gap: 80px;
  align-items: center;
  position: relative;
  z-index: 1;
}

.compliance-description {
  color: var(--text-light);
  font-size: 1.05rem;
  line-height: 1.8;
  margin-bottom: 20px;
}

.compliance-principles {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin: 36px 0;
}

.principle-item {
  padding: 24px;
  border-radius: 20px;
  display: flex;
  gap: 18px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.principle-item:hover {
  transform: translateY(-4px);
}

.principle-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(52, 152, 255, 0.15) 0%, rgba(15, 39, 64, 0.08) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--secondary-color);
  flex-shrink: 0;
  box-shadow: 0 2px 10px rgba(52, 152, 255, 0.1);
}

.principle-icon svg {
  width: 28px;
  height: 28px;
}

.principle-content h4 {
  font-size: 1.1rem;
  color: var(--primary-color);
  margin-bottom: 6px;
  font-weight: 700;
}

.principle-content p {
  color: var(--text-light);
  font-size: 0.92rem;
  line-height: 1.6;
}

.compliance-actions {
  margin-top: 30px;
}

.btn {
  display: inline-block;
  padding: 14px 36px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.3s ease;
  text-decoration: none;
}

.btn-outline {
  border: 2px solid var(--secondary-color);
  color: var(--secondary-color);
  background: rgba(52, 152, 255, 0.05);
  backdrop-filter: blur(10px);
}

.btn-outline:hover {
  background: var(--secondary-color);
  color: var(--white);
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(52, 152, 255, 0.3);
}

.compliance-image {
  position: relative;
}

.image-card {
  width: 100%;
  aspect-ratio: 3/4;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.3) 100%);
  border-radius: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--secondary-color);
  position: relative;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.image-card:hover {
  transform: translateY(-6px);
}

.image-card::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  right: -50%;
  bottom: -50%;
  background:
    radial-gradient(circle at 30% 30%, rgba(52, 152, 255, 0.18) 0%, transparent 50%),
    radial-gradient(circle at 70% 70%, rgba(15, 39, 64, 0.1) 0%, transparent 50%);
  animation: shimmer 8s ease-in-out infinite;
  pointer-events: none;
}

@keyframes shimmer {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  33% { transform: translate(3%, 2%) rotate(3deg); }
  66% { transform: translate(-2%, 3%) rotate(-2deg); }
}

.image-card svg {
  opacity: 1;
  filter: drop-shadow(0 2px 4px rgba(52, 152, 255, 0.2));
  position: relative;
  z-index: 1;
}

.image-card p {
  margin-top: 20px;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--primary-color);
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
  position: relative;
  z-index: 1;
}

.compliance-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: relative;
  z-index: 1;
  filter: drop-shadow(0 2px 4px rgba(52, 152, 255, 0.2));
}

@media (max-width: 1024px) {
  .compliance-content {
    grid-template-columns: 1fr;
    gap: 50px;
  }
  .compliance-image {
    order: -1;
  }
  .image-card {
    aspect-ratio: 16/9;
    max-width: 500px;
    margin: 0 auto;
  }
}

@media (max-width: 768px) {
  .compliance {
    padding: 80px 0;
  }
  .compliance-content {
    gap: 40px;
  }
  .compliance-principles {
    grid-template-columns: 1fr;
    gap: 16px;
    margin: 28px 0;
  }
  .principle-item {
    padding: 20px;
  }
  .compliance-description {
    font-size: 1rem;
  }
  .compliance-actions {
    margin-top: 24px;
  }
  .compliance-actions .btn {
    padding: 12px 30px;
    font-size: 1rem;
    display: inline-block;
  }
}
</style>
